/**
 * @fileoverview "Nothing to show" state shared by every sales table.
 * @description A compact state block — icon tile, title, one sentence of help, and an optional
 * "Clear filters" action — used wherever a filterable sales table has no rows. `clearLabel`/
 * `onClear` are both-or-neither: omit them for a state that isn't filter-induced (e.g. "no banks
 * are currently assigned to you" at all, rather than "no banks match these filters"), where a
 * page's own filter bar may already have its own always-visible Clear filters button and a second
 * one here would be both redundant and, worse, offered when there's nothing to actually clear.
 * A filter-induced state shows the shared "try a different search" hint and a search icon unless
 * the page passes its own `description`/`icon`; a state with no filters shows `description` only
 * when the page gives one.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import StateMessage from '@/shared/components/molecules/StateMessage'

import type { IconName } from '@/shared/components/atoms/Icon'
import type { StateTone } from '@/shared/components/molecules/StateMessage'

interface TableEmptyStateProps {
  message: string
  description?: string
  icon?: IconName
  tone?: StateTone
  clearLabel?: string
  onClear?: () => void
}

/**
 * @component
 * @param {TableEmptyStateProps} props - The empty-state copy and the optional clear-filters
 * handler.
 * @param {string} props.message - The state's title (e.g. "No users match these filters").
 * @param {string} [props.description] - One sentence of help under the title. Defaults, for a
 * filter-induced state, to the shared "try a different search" hint.
 * @param {IconName} [props.icon] - The icon in the tile (search when filter-induced, inbox
 * otherwise).
 * @param {StateTone} [props.tone] - The tile's tint (quiet when filter-induced, info otherwise).
 * @param {string} [props.clearLabel] - The clear-filters button's label, if this state is
 * filter-induced.
 * @param {() => void} [props.onClear] - Called when the clear-filters button is clicked. Omit
 * together with `clearLabel` to render just the icon and text, with no action.
 */
function TableEmptyState({
  message,
  description,
  icon,
  tone,
  clearLabel,
  onClear,
}: TableEmptyStateProps) {
  const { t } = useTranslation()
  const filterInduced = Boolean(onClear && clearLabel)

  return (
    <StateMessage
      compact
      testId="table-empty-state"
      icon={icon ?? (filterInduced ? 'search' : 'inbox')}
      tone={tone ?? (filterInduced ? 'quiet' : 'info')}
      title={message}
      description={description ?? (filterInduced ? t('sales.emptyState.filteredHint') : undefined)}
      actions={
        filterInduced ? (
          <button
            type="button"
            onClick={onClear}
            data-testid="table-empty-state-clear-button"
            className="cursor-pointer rounded-lg border border-input-border bg-white px-3.5 py-2 text-compact font-semibold text-primary hover:border-primary"
          >
            {clearLabel}
          </button>
        ) : undefined
      }
    />
  )
}

export default TableEmptyState
